import { useState, useEffect, useRef } from "react";
// All icons are inline SVGs to avoid global CSS size overrides

/* ── All inline SVG icon components ─────────────────────────────────────── */
const IconLightbulb = ({ color = "#3b82f6" }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        style={{ width: 13, height: 13, minWidth: 13, minHeight: 13, display: "block", flexShrink: 0 }}>
        <line x1="9" y1="18" x2="15" y2="18"/>
        <line x1="10" y1="22" x2="14" y2="22"/>
        <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"/>
    </svg>
);

const IconRefresh = ({ color = "#3b82f6" }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        style={{ width: 11, height: 11, minWidth: 11, minHeight: 11, display: "block", flexShrink: 0 }}>
        <polyline points="23 4 23 10 17 10"/>
        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
    </svg>
);

const IconSpinner = ({ color = "#3b82f6" }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round"
        style={{ width: 11, height: 11, minWidth: 11, minHeight: 11, display: "block", flexShrink: 0, animation: "ip-spin 0.8s linear infinite" }}>
        <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
    </svg>
);

const IconLoaderLg = ({ color = "#64748b" }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round"
        style={{ width: 14, height: 14, minWidth: 14, minHeight: 14, display: "block", flexShrink: 0, animation: "ip-spin 0.8s linear infinite" }}>
        <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
    </svg>
);

/* ── Minimal markdown → JSX renderer ─────────────────────────────────────── */
function renderMarkdown(text) {
    if (!text) return null;
    const lines = text.split("\n");
    const elements = [];
    let bulletBuffer = [];
    let key = 0;

    const flushBullets = () => {
        if (bulletBuffer.length === 0) return;
        elements.push(
            <ul key={`ul-${key++}`} style={S.ul}>
                {bulletBuffer.map((b, i) => (
                    <li key={i} style={S.li}>
                        <span style={S.liBullet}>◆</span>
                        <span style={S.liText} dangerouslySetInnerHTML={{ __html: parseInline(b) }} />
                    </li>
                ))}
            </ul>
        );
        bulletBuffer = [];
    };

    for (const raw of lines) {
        const line = raw.trim();
        if (!line) { flushBullets(); continue; }
        if (/^[-*•]\s+/.test(line)) { bulletBuffer.push(line.replace(/^[-*•]\s+/, "")); continue; }
        flushBullets();
        if (/^\*\*[^*]+\*\*$/.test(line)) {
            elements.push(<div key={key++} style={S.heading}>{line.replace(/\*\*/g, "")}</div>);
            continue;
        }
        elements.push(<p key={key++} style={S.paragraph} dangerouslySetInnerHTML={{ __html: parseInline(line) }} />);
    }
    flushBullets();
    return elements;
}

function parseInline(text) {
    return text
        .replace(/\*\*(.+?)\*\*/g, '<strong style="font-weight:650;color:#0f172a">$1</strong>')
        .replace(/_(.+?)_/g, '<em>$1</em>');
}

/* ── Inline SVG icons — fixed 14px, immune to lucide sizing issues ────────── */
const IconWarn = () => (
    <svg viewBox="0 0 24 24" fill="none"
        stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        style={{ width: 14, height: 14, minWidth: 14, minHeight: 14, flexShrink: 0, display: "block" }}>
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
);

const IconBarChart = () => (
    <svg viewBox="0 0 24 24" fill="none"
        stroke="#94a3b8" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
        style={{ width: 20, height: 20, minWidth: 20, minHeight: 20, display: "block" }}>
        <line x1="18" y1="20" x2="18" y2="10"/>
        <line x1="12" y1="20" x2="12" y2="4"/>
        <line x1="6"  y1="20" x2="6"  y2="14"/>
        <line x1="2"  y1="20" x2="22" y2="20"/>
    </svg>
);

/* ── Distribution mini-bar ───────────────────────────────────────────────── */
const DistBar = ({ kpi, percentage, accent }) => {
    const pct = Math.min(100, Math.max(0, percentage || 0));
    const [animated, setAnimated] = useState(false);
    useEffect(() => { const t = setTimeout(() => setAnimated(true), 80); return () => clearTimeout(t); }, []);
    return (
        <div style={{ marginBottom: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 3 }}>
                <span style={{ fontSize: 11, fontWeight: 550, color: "#374151", fontFamily: "'DM Sans', system-ui, sans-serif", maxWidth: "65%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {kpi}
                </span>
                <span style={{ fontSize: 10.5, fontWeight: 700, color: accent, fontFamily: "'DM Mono', monospace", letterSpacing: "0.04em" }}>
                    {pct.toFixed(1)}%
                </span>
            </div>
            <div style={{ height: 5, background: "#f1f5f9", borderRadius: 99, overflow: "hidden" }}>
                <div style={{ height: "100%", width: animated ? `${pct}%` : "0%", background: `linear-gradient(90deg, ${accent}cc, ${accent})`, borderRadius: 99, transition: "width 0.7s cubic-bezier(0.4,0,0.2,1)" }} />
            </div>
        </div>
    );
};

/* ── Inline stat icons ── */
const IconTrendUp = ({ color }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
        style={{ width: 11, height: 11, minWidth: 11, minHeight: 11, flexShrink: 0, display: "block" }}>
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>
    </svg>
);
const IconTrendDown = ({ color }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
        style={{ width: 11, height: 11, minWidth: 11, minHeight: 11, flexShrink: 0, display: "block" }}>
        <polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/><polyline points="17 18 23 18 23 12"/>
    </svg>
);
/* ── Stat chip ───────────────────────────────────────────────────────────────
   A "Zero" chip used to sit beside these, reading the length of
   zero_contributors straight out of the payload. It counted period-and-series
   pairs while calling them sources, so a single reporting gap on a two-series
   chart read as "2 sources", one gap read as "1 sources", and neither number
   meant what it said. The narrative below already names what stopped and
   when, in sentences that are right. */
const CHIP_ICONS = {
    Top:    (accent) => <IconTrendUp color={accent} />,
    Lowest: (accent) => <IconTrendDown color={accent} />,
};
const StatChip = ({ label, value, accent }) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 2, padding: "8px 10px", background: `${accent}0a`, border: `1px solid ${accent}20`, borderRadius: 10, flex: "1 1 0", minWidth: 0, overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            {CHIP_ICONS[label]?.(accent)}
            <span style={{ fontSize: 9.5, fontWeight: 700, color: accent, fontFamily: "'DM Mono', monospace", letterSpacing: "0.06em", textTransform: "uppercase" }}>{label}</span>
        </div>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#0f172a", fontFamily: "'DM Sans', system-ui, sans-serif", letterSpacing: "-0.02em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {value || "—"}
        </span>
    </div>
);

/* ── Skeleton ────────────────────────────────────────────────────────────── */
const Skeleton = () => (
    <>
        <style>{`@keyframes ip-shimmer{0%,100%{background-position:200% 0}50%{background-position:-200% 0}}`}</style>
        <div style={{ padding: "20px 18px", display: "flex", flexDirection: "column", gap: 10 }}>
            {[70, 90, 55, 80, 65, 40].map((w, i) => (
                <div key={i} style={{ height: i === 0 ? 14 : 11, width: `${w}%`, borderRadius: 6, background: "linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)", backgroundSize: "200% 100%", animation: `ip-shimmer 1.5s ease ${i * 0.08}s infinite` }} />
            ))}
            <div style={{ marginTop: 6, display: "flex", gap: 6 }}>
                {[1, 2, 3].map(i => (
                    <div key={i} style={{ height: 52, flex: 1, borderRadius: 10, background: "linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)", backgroundSize: "200% 100%", animation: `ip-shimmer 1.5s ease ${i * 0.1}s infinite` }} />
                ))}
            </div>
        </div>
    </>
);

/* ── Centred placeholder (empty / no-data / initial) ─────────────────────── */
const Placeholder = ({ icon, title, subtitle }) => (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, gap: 12, padding: "32px 24px", textAlign: "center" }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: "#f8fafc", border: "1.5px dashed #cbd5e1", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {icon}
        </div>
        <div>
            <p style={{ margin: "0 0 5px", fontSize: 13, fontWeight: 650, color: "#374151", fontFamily: "'DM Sans', system-ui, sans-serif", letterSpacing: "-0.015em" }}>
                {title}
            </p>
            <p style={{ margin: 0, fontSize: 11.5, color: "#94a3b8", lineHeight: 1.55, fontFamily: "'DM Sans', system-ui, sans-serif", fontWeight: 400 }}>
                {subtitle}
            </p>
        </div>
    </div>
);

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  InsightsPanel                                                              */
/* ═══════════════════════════════════════════════════════════════════════════ */
const InsightsPanel = ({ insights, loading, error, onRefresh, accent = "#3b82f6" }) => {
    const [visible, setVisible] = useState(false);
    const prevRef = useRef(null);

    useEffect(() => {
        if (insights && insights !== prevRef.current) {
            setVisible(false);
            const t = setTimeout(() => { setVisible(true); prevRef.current = insights; }, 60);
            return () => clearTimeout(t);
        }
        if (insights) setVisible(true);
    }, [insights]);

    const structured = insights?.structured;
    const narrative  = insights?.narrative;
    const topDist    = structured?.distribution?.slice(0, 5) || [];

    return (
        <>
            <style>{`
                @keyframes ip-dot-pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.4;transform:scale(.75)} }
                @keyframes ip-spin      { to { transform: rotate(360deg); } }
            `}</style>

            <div style={S.panel}>

                {/* ── HEADER ── */}
                <div style={S.header}>
                    <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                        <div style={{ width: 26, height: 26, borderRadius: 8, background: `${accent}15`, border: `1.5px solid ${accent}30`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <IconLightbulb color={accent} />
                        </div>
                        <span style={S.headerTitle}>Insights</span>
                        {insights && !insights._empty && !loading && (
                            <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#22c55e", animation: "ip-dot-pulse 2.5s ease-in-out infinite", flexShrink: 0 }} />
                        )}
                    </div>

                    <button
                        onClick={onRefresh}
                        disabled={loading}
                        style={{ display: "flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 7, border: `1px solid ${accent}30`, background: loading ? `${accent}08` : `${accent}0d`, color: loading ? `${accent}80` : accent, fontSize: 11, fontWeight: 600, fontFamily: "'DM Mono', monospace", letterSpacing: "0.05em", cursor: loading ? "not-allowed" : "pointer", transition: "all 0.15s", outline: "none" }}
                        onMouseOver={e => { if (!loading) e.currentTarget.style.background = `${accent}18`; }}
                        onMouseOut={e => { if (!loading) e.currentTarget.style.background = `${accent}0d`; }}
                    >
                        {loading ? <IconSpinner color={loading ? `${accent}80` : accent} /> : <IconRefresh color={accent} />}
                        {loading ? "Loading" : "Refresh"}
                    </button>
                </div>

                {/* ── Accent divider ── */}
                <div style={{ height: 1, background: `linear-gradient(90deg, ${accent}25, transparent)`, flexShrink: 0 }} />

                {/* ── BODY ── */}
                <div style={S.body}>

                    {/* Skeleton — first load only */}
                    {loading && !insights && <Skeleton />}

                    {/* Error state — centered vertically */}
                    {error && !loading && (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, gap: 10, padding: "24px 18px", textAlign: "center" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, width: "100%" }}>
                                <IconWarn />
                                <span style={{ fontSize: 12, color: "#dc2626", fontWeight: 500, fontFamily: "'DM Sans', system-ui, sans-serif", flex: 1, textAlign: "left", lineHeight: 1.4 }}>
                                    Failed to load insights
                                </span>
                                <button onClick={onRefresh} style={{ flexShrink: 0, background: "none", border: "none", fontSize: 11, fontWeight: 700, color: "#dc2626", cursor: "pointer", fontFamily: "'DM Mono', monospace", letterSpacing: "0.04em", textDecoration: "underline", textUnderlineOffset: 2 }}>
                                    Retry
                                </button>
                            </div>
                            <p style={{ margin: 0, fontSize: 11.5, color: "#94a3b8", lineHeight: 1.55, fontFamily: "'DM Sans', system-ui, sans-serif", maxWidth: 220 }}>
                                Unable to generate insights. Check your connection and try again.
                            </p>
                        </div>
                    )}

                    {/* Initial — before any fetch */}
                    {!loading && !error && !insights && (
                        <Placeholder icon={<IconBarChart />} title="No insights yet" subtitle="Insights will appear once data loads" />
                    )}

                    {/* Empty — API returned no-data string */}
                    {insights?._empty && (
                        <Placeholder icon={<IconBarChart />} title="No insights available" subtitle={insights.message || "No meaningful data available to generate insights."} />
                    )}

                    {/* ── Real insights content ── */}
                    {insights && !insights._empty && (
                        <div style={{ opacity: visible ? 1 : 0, transition: "opacity 0.35s ease", display: "flex", flexDirection: "column" }}>

                            {/* Stat chips */}
                            {structured && (
                                <div style={{ padding: "14px 16px 12px", display: "flex", gap: 6 }}>
                                    {structured.top_kpi && <StatChip label="Top" value={structured.top_kpi.name} accent={accent} />}
                                    {structured.lowest_kpi && <StatChip label="Lowest" value={structured.lowest_kpi.name} accent="#64748b" />}
                                </div>
                            )}

                            {/* Distribution bars */}
                            {topDist.length > 0 && (
                                <div style={{ padding: "0 16px 14px" }}>
                                    <div style={{ fontSize: 9.5, fontWeight: 700, color: "#94a3b8", fontFamily: "'DM Mono', monospace", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>
                                        Distribution
                                    </div>
                                    {topDist.map((d, i) => <DistBar key={i} kpi={d.kpi} percentage={d.percentage} accent={accent} />)}
                                </div>
                            )}

                            {/* Divider */}
                            {(topDist.length > 0 || structured) && narrative && (
                                <div style={{ height: 1, background: "#f1f5f9", margin: "0 16px 14px" }} />
                            )}

                            {/* Narrative */}
                            {narrative && (
                                <div style={{ padding: "0 16px 16px" }}>
                                    <div style={{ fontSize: 9.5, fontWeight: 700, color: "#94a3b8", fontFamily: "'DM Mono', monospace", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>
                                        Analysis
                                    </div>
                                    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                                        {renderMarkdown(narrative)}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Refresh overlay over existing content */}
                    {loading && insights && !insights._empty && (
                        <div style={{ position: "absolute", inset: 0, background: "rgba(255,255,255,0.65)", backdropFilter: "blur(2px)", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontSize: 11, color: "#64748b", fontFamily: "'DM Mono', monospace", letterSpacing: "0.04em", borderRadius: "inherit", zIndex: 10 }}>
                            <IconLoaderLg color="#64748b" />
                            Refreshing…
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default InsightsPanel;

/* ── Styles ──────────────────────────────────────────────────────────────── */
const S = {
    panel: {
        display: "flex",
        flexDirection: "column",
        background: "#ffffff",
        borderRadius: 14,
        border: "1px solid #e8edf2",
        boxShadow: "0 1px 3px rgba(15,23,42,0.05), 0 4px 16px rgba(15,23,42,0.04)",
        overflow: "hidden",
        height: "100%",
        flex: 1,
        position: "relative",
        fontFamily: "'DM Sans', system-ui, sans-serif",
    },
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "10px 14px",
        background: "#fafafa",
        flexShrink: 0,
    },
    headerTitle: {
        fontSize: 13, fontWeight: 700, color: "#0f172a", letterSpacing: "-0.02em",
    },
    body: {
        flex: 1,
        overflowY: "auto",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        scrollbarWidth: "thin",
        scrollbarColor: "#e2e8f0 transparent",
    },
    heading: {
        fontSize: 13.5, fontWeight: 700, color: "#0f172a", letterSpacing: "-0.025em",
        lineHeight: 1.35, marginBottom: 8, fontFamily: "'DM Sans', system-ui, sans-serif",
    },
    paragraph: {
        fontSize: 12.5, lineHeight: 1.7, color: "#374151", margin: "0 0 10px 0",
        fontFamily: "'DM Sans', system-ui, sans-serif", fontWeight: 420, letterSpacing: "-0.005em",
    },
    ul: { listStyle: "none", padding: 0, margin: "0 0 10px 0", display: "flex", flexDirection: "column", gap: 6 },
    li: { display: "flex", alignItems: "flex-start", gap: 7 },
    liBullet: { fontSize: 6, color: "#94a3b8", marginTop: 6, flexShrink: 0 },
    liText: { fontSize: 12.5, lineHeight: 1.65, color: "#374151", fontFamily: "'DM Sans', system-ui, sans-serif", fontWeight: 420, letterSpacing: "-0.005em" },
};